import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Battery, Zap, Mountain, Camera, AlertTriangle } from 'lucide-react';
import './GuidePage.css';

export default function SkyeStory() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🏔️ Isle of Skye mit dem E-Bike - Thomas' Abenteuer</h1>
        
        <div className="info-box">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div>
              <User size={24} />
            </div>
            <div>
              <p><strong>Autor:</strong> Thomas K., 42 Jahre</p>
              <p><strong>Tour:</strong> Isle of Skye Rundtour</p>
              <p><strong>Dauer:</strong> 4 Tage (August 2024)</p>
              <p><strong>Distanz:</strong> 170 km, ~3.000 Höhenmeter</p>
              <p><strong>Rad:</strong> E-Bike (Specialized Turbo Vado, 90Nm Motor)</p>
            </div>
          </div>
        </div>

        <section>
          <h2>Warum Skye mit E-Bike?</h2>
          <p>
            Ich bin seit 20 Jahren Radfahrer - aber <strong>Skye ist anders</strong>. 20% Steigung 
            am Quiraing Pass? Single-Track Roads mit Gegenwind? Das wollte ich genießen, nicht 
            nur überleben. Also: E-Bike!
          </p>
          <p>
            <strong>Spoiler:</strong> Beste Entscheidung ever! Ohne Motor hätte ich die Hälfte der 
            Routen verpasst oder nur mit Schmerzen geschafft. Mit E-Bike: Magisch! ⚡
          </p>
        </section>

        <section>
          <h2>Tag 1: Armadale → Portree (60km, 900m HM)</h2>
          
          <p>
            <strong>Fähre Mallaig → Armadale:</strong> 30 Minuten, smooth. Rad wird separat 
            verladen (£10 extra, lohnt sich vs. 5h Fahrt über Skye Bridge!).
          </p>

          <h3>Die Realität trifft hart</h3>
          <p>
            Kilometer 1-10: "Ach, das geht ja!" 😊<br />
            Kilometer 20-30: "Hmm, doch hilliger als gedacht..." 😅<br />
            Kilometer 40: "WTF, schon WIEDER bergauf?!" 😤
          </p>
          <p>
            <strong>Skye ist RELENTLESS!</strong> Kaum flache Abschnitte. Es geht hoch, runter, 
            hoch, runter. Immer und immer wieder. Selbst mit E-Bike: Anstrengend!
          </p>

          <div className="warning-box">
            <Battery size={20} />
            <strong>Akku-Warnung!</strong> Nach 50km war mein Akku bei 30%. Ich hatte Panik! 
            Lerneffekt: Skye's Höhenmeter fressen Akku! Eco-Modus konsequent nutzen.
          </div>

          <h3>Portree Ankunft</h3>
          <p>
            <strong>Portree = Touristenhölle im August!</strong> 😱 Aber auch: Hübsch, gute 
            Restaurants, erster richtiger Supermarkt auf Skye. Fish & Chips am Hafen = 
            Pflichtprogramm!
          </p>

          <p><strong>Übernachtung:</strong> Portree B&B (6 Monate vorgebucht! August = voll!)</p>
        </section>

        <section>
          <h2>Tag 2: Portree → The Storr → Quiraing → Uig (85km, 1.200m HM)</h2>
          
          <p>
            <strong>DER EPISCHE TAG!</strong> Ich wollte "die große Runde" machen: Storr UND 
            Quiraing an einem Tag. Freunde sagten: "Unmöglich!" Ich sagte: "E-Bike sagt: Hold 
            my beer!" 🍺
          </p>

          <h3>The Storr (7:00 Uhr Start)</h3>
          <div className="pro-tip">
            <h3><Camera size={20} /> Früh starten = Lebenshack!</h3>
            <p>
              7 Uhr am Storr: <strong>Ich war ALLEINE!</strong> Kein Tourist Bus, keine 
              Menschenmassen. Nur ich, der Old Man of Storr, und dramatisches Morgenlicht. 
              <strong>Unbezahlbar!</strong>
            </p>
            <p>
              (Um 10 Uhr kamen die Busse - dann war's vorbei mit Ruhe)
            </p>
          </div>

          <h3>Quiraing Pass - Die Legende</h3>
          <p>
            Der Quiraing ist... wie soll ich das beschreiben... <strong>BRUTAL und WUNDERSCHÖN 
            zugleich!</strong>
          </p>
          
          <div className="checklist">
            <div className="check-item">📊 Steigung: 20% (!) über mehrere Kilometer</div>
            <div className="check-item">💪 E-Bike Modus: Turbo (ohne geht's nicht!)</div>
            <div className="check-item">😰 Schweiß-Level: Extreme (trotz Motor!)</div>
            <div className="check-item">🤩 Aussicht: MINDBLOWING (worth it x1000!)</div>
          </div>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Ohne E-Bike hätte ich geschoben!</strong> 100%. Die Steigung ist kein Witz. 
            Ich sah zwei "normale" Radfahrer schieben - und die sahen fit aus! Mit E-Bike: 
            Turbo an, treten, genießen. Immer noch hart, aber machbar!
          </div>

          <h3>Die Abfahrt</h3>
          <p>
            Nach dem Pass: <strong>Abfahrt nach Uig = INSANE!</strong> Single-Track, kurvig, 
            15% Gefälle. Ich musste bremsen wie verrückt (Bremsen haben nachher geraucht 😅). 
            Aber: So viel Spaß!
          </p>

          <p><strong>Übernachtung:</strong> Uig (winzig, nur 2 B&Bs - voll!)</p>
        </section>

        <section>
          <h2>Tag 3: Uig → Dunvegan → Portree (65km, 700m HM)</h2>
          
          <p>
            <strong>Entspannterer Tag</strong> (nach gestern brauchte ich das!). Westküste von 
            Skye = anders als Trotternish. Weniger dramatisch, mehr grün & ruhig.
          </p>

          <h3>Dunvegan Castle</h3>
          <p>
            Ältestes bewohntes Schloss Schottlands (seit 800 Jahren MacLeod-Clan!). Gärten 
            sind schön, Bootstour zu Seals möglich (ich hab's ausgelassen - zu müde 😴).
          </p>

          <h3>Die Realität von Skye-Wetter</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <p>
              Heute: 4 Jahreszeiten in 6 Stunden!<br />
              9 Uhr: Sonnenschein ☀️<br />
              10 Uhr: Wolken ☁️<br />
              11 Uhr: Regen 🌧️<br />
              12 Uhr: STURM 💨 (musste 30 Min. im Pub warten!)<br />
              13 Uhr: Wieder Sonne ☀️<br />
            </p>
            <p>
              <strong>Skye-Lektion:</strong> Regenkleidung IMMER dabei haben. Nicht im 
              Gepäck verstaut - sondern griffbereit am Lenker!
            </p>
          </div>

          <p><strong>Zurück in Portree:</strong> 2. Nacht, dieselbe B&B (Glück gehabt!)</p>
        </section>

        <section>
          <h2>Tag 4: Portree → Elgol → Sligachan → Portree (60km, 800m HM)</h2>
          
          <h3>Elgol - Der Geheimtipp</h3>
          <p>
            <strong>Elgol = unterschätzt!</strong> Während alle zum Storr rennen, ist Elgol 
            leer & spektakulär. <strong>Blick auf die Cuillin Mountains = episch!</strong>
          </p>

          <div className="pro-tip">
            <h3><Mountain size={20} /> Cuillin Mountains</h3>
            <p>
              Die Cuillins sind die dramatischsten Berge Schottlands. Scharf, gezackt, schwarz 
              (Gabbro-Gestein). Von Elgol: Perfekter Blick! Bootstour zu Loch Coruisk möglich 
              (£25, 2h - hab's gemacht, lohnt sich!).
            </p>
          </div>

          <h3>Single-Track nach Elgol</h3>
          <p>
            Die Straße nach Elgol ist <strong>hardcore Single-Track</strong>. Eng, kurvig, 
            Passing Places alle 200m. Ich musste 10x rechts ranfahren für Autos. Aber: Wenig 
            Verkehr, weil Touristen Angst haben 😂
          </p>

          <h3>Sligachan Hotel</h3>
          <p>
            Historisches Hotel, mitten im Nirgendwo, am Fuße der Cuillins. Ich hab hier Mittag 
            gemacht: <strong>Haggis & Whisky</strong> (touristisch, aber hey - ich bin Tourist!).
          </p>

          <p><strong>Ende:</strong> Zurück nach Portree, letzter Abend. Morgen: Fähre zurück.</p>
        </section>

        <section>
          <h2>E-Bike Erfahrungen auf Skye</h2>
          
          <h3><Zap size={20} /> Motor & Akku</h3>
          <p>
            Mein E-Bike: <strong>Specialized Turbo Vado</strong> mit 90Nm Motor, 710Wh Akku.
          </p>

          <div className="data-grid">
            <div className="data-item">
              <strong>Eco-Modus:</strong> 50-60km Reichweite (flach)<br />
              Auf Skye: 40-45km (Höhenmeter!)
            </div>
            <div className="data-item">
              <strong>Tour-Modus:</strong> 40-50km Reichweite<br />
              Auf Skye: 30-35km (meine meist-genutzte Einstellung)
            </div>
            <div className="data-item">
              <strong>Turbo-Modus:</strong> 30-40km Reichweite<br />
              Auf Skye: 25-30km (nur für Quiraing!)
            </div>
            <div className="data-item">
              <strong>Ohne Motor:</strong> Möglich, aber... warum?! 😅<br />
              (Testete es am Tag 3 - nach 5 Min. wieder Motor an!)
            </div>
          </div>

          <h3>Lade-Strategie</h3>
          <ul>
            <li><strong>Jede Nacht:</strong> Voll laden (B&Bs hatten alle Steckdosen)</li>
            <li><strong>Mittagspause:</strong> Wenn möglich nachlade (Cafés/Pubs fragen!)</li>
            <li><strong>Ersatz-Akku:</strong> Hatte ich nicht - wäre aber Gold wert gewesen!</li>
            <li><strong>Powerbank:</strong> Für Handy/GPS (10.000 mAh reicht)</li>
          </ul>

          <div className="warning-box">
            <Battery size={20} />
            <strong>Akku-Panik-Moment (Tag 2):</strong><br />
            Nach Quiraing: Akku bei 10%, noch 20km bis Uig. Ich schaltete auf Eco, trat wie 
            verrückt, und betete! 😅 Kam mit 2% an. <strong>Lerneffekt: Immer 20% Reserve 
            einplanen!</strong>
          </div>
        </section>

        <section>
          <h2>Was ich über Skye gelernt habe</h2>
          
          <h3>Die 10 Skye-Wahrheiten</h3>
          <ol>
            <li><strong>Skye ist KEIN Anfänger-Terrain!</strong> Auch mit E-Bike: Anstrengend. 
            Ohne: Brutal.</li>
            <li><strong>20% Steigung = kein Witz!</strong> Quiraing war härter als erwartet. 
            Turbo-Modus + treten nötig!</li>
            <li><strong>Single-Track Etiquette lernen!</strong> Rechts ran, Autos vorbeilassen, 
            danken. Immer!</li>
            <li><strong>Früh starten = Game Changer!</strong> 7 Uhr am Storr: Alleine. 10 Uhr: 
            Touristenmassen.</li>
            <li><strong>Wetter ist EXTREM wechselhaft!</strong> Regenkleidung griffbereit, nicht 
            verstaut!</li>
            <li><strong>Midges sind REAL!</strong> Ich hatte August = Peak Season. Midge-Netz + 
            Spray = Überlebenswichtig!</li>
            <li><strong>Unterkünfte VORBUCHEN!</strong> Skye ist klein, Touristen viele. 
            Ich buchte 6 Monate vorher - gut so!</li>
            <li><strong>Akku-Management ist kritisch!</strong> Höhenmeter fressen Akku. Immer 
            20% Reserve einplanen.</li>
            <li><strong>Elgol ist unterschätzt!</strong> Während alle zum Storr fahren, ist 
            Elgol leer & spektakulär.</li>
            <li><strong>E-Bike = Beste Entscheidung!</strong> Ohne hätte ich die Hälfte verpasst 
            oder nur gelitten.</li>
          </ol>
        </section>

        <section>
          <h2>Würde ich es nochmal machen?</h2>
          
          <p>
            <strong>Absolut! Aber mit Änderungen:</strong>
          </p>
          <ul>
            <li><strong>Ersatz-Akku mitnehmen:</strong> £500 teuer, aber Stress-frei!</li>
            <li><strong>5 Tage statt 4:</strong> Mehr Zeit für Wanderungen (Fairy Pools hab ich 
            verpasst!)</li>
            <li><strong>September statt August:</strong> Weniger Touristen, weniger Midges</li>
            <li><strong>Noch früher starten:</strong> 6:30 Uhr statt 7 Uhr (für Fotos!)</li>
          </ul>

          <h3>Mein Fazit</h3>
          <p>
            Skye mit E-Bike war <strong>die beste Radtour meines Lebens</strong> (und ich bin 
            schon viel gefahren!). Ja, es war hart. Ja, ich habe geflucht (vor allem am Quiraing). 
            Aber: <strong>Die Landschaft, die Dramatik, das Gefühl "Ich hab's geschafft!" - 
            unbezahlbar!</strong>
          </p>
          <p>
            <strong>E-Bike-Skeptiker werden sagen:</strong> "Das ist Schummeln!" Ich sage: 
            <strong>"Das ist smart!"</strong> Ich wollte Skye erleben, nicht nur überleben. 
            Mission accomplished! ✅
          </p>
        </section>

        <div className="pro-tip">
          <h3>🎯 Thomas' Top 5 Skye-Momente</h3>
          <ol>
            <li><strong>Quiraing Pass Gipfel:</strong> 20% geschafft, Aussicht = Wahnsinn!</li>
            <li><strong>Storr um 7 Uhr:</strong> Alleine, Morgenlicht, magisch</li>
            <li><strong>Elgol Cuillin-Blick:</strong> Bootstour zu Loch Coruisk</li>
            <li><strong>Quiraing Abfahrt:</strong> Single-Track Downhill, Adrenalin pur!</li>
            <li><strong>Sligachan Haggis & Whisky:</strong> Mitten im Nirgendwo, perfekt!</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Hilfreiche Guides</h4>
          <ul>
            <li><Link to="/wissen/region/islands">🏝️ Islands-Guide</Link> - Skye Routen-Details</li>
            <li><Link to="/wissen/ebike">⚡ E-Bike Basics</Link> - Akku-Management</li>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Was du brauchst</li>
          </ul>
        </div>
      </article>
    </div>
  );
}
